import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useEffect, useState } from 'react';
import UsersPage from './pages/users.page';
import Games2Page from './pages/games.page';
import AppLayout from './components/AppLayout';
import GamePage from './pages/game/[id]';
import RegisterPage from './pages/register.page';
import LoginPage from './pages/login.page';
import 'bulma/css/bulma.min.css';
import './theme.css';
import UserPage from './pages/user/[id]';
import PlayPage from './pages/play.page';
import SetupGamePage from './pages/setup-game.page';
import MyGamesPage from './pages/my-games.page';
import CampaignPage from './pages/campaign.page';
import CampaignMissionPage from './pages/campaign/[id]';
import EditorPage from './pages/editor.page';
import EditorMapPage from './pages/editor/[id]';
import {
  AuthContext,
  AuthUser,
  addTokenChangeListener,
  initAuth,
  removeTokenChangeListener,
  setAccessToken,
  useAuth,
} from './api';
import LogoutPage from './pages/logout-page';
import HomePage from './pages/home.page';
import MessagesPage from './pages/messages.page';
import ForgotPasswordPage from './pages/forgot-password.page';
import ResetPasswordPage from './pages/reset-password.page';
import AccountPage from './pages/account.page';
import SettingsPage from './pages/settings.page';
import VerifyEmailPage from './pages/verify-email.page';

// Guards a group of routes: unauthenticated users are sent to /login, with the
// page they were trying to reach stashed in location state so login can return
// them there afterwards.
function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();
  if (!auth) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

// Guards the auth-flow pages (login/register): an already-authenticated user has
// no reason to see them, so bounce them to wherever they were headed (or /my-games).
function PublicOnly() {
  const auth = useAuth();
  const location = useLocation();
  if (auth) {
    const from = (location.state as { from?: Location } | null)?.from?.pathname;
    return <Navigate to={from ?? '/my-games'} replace />;
  }
  return <Outlet />;
}

function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('access_token');
    if (token) {
      setAccessToken(token);
      navigate('/my-games', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate, searchParams]);

  return <div className="has-text-centered p-6">Signing in...</div>;
}

function App() {
  const [auth, setAuth] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initAuth().then((user) => {
      setAuth(user);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const callback = (parsedToken: AuthUser | null) => setAuth(parsedToken);
    addTokenChangeListener(callback);
    return () => removeTokenChangeListener(callback);
  }, []);

  if (loading) {
    return (
      <div
        className="has-text-centered"
        style={{ padding: '100px 20px', color: '#999' }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div className="App">
      <AuthContext.Provider value={auth}>
        <Routes>
          {/* Full-screen, auth-required (no AppLayout chrome) */}
          <Route element={<RequireAuth />}>
            <Route path="/games/:id" element={<GamePage />} />
            <Route path="/editor/:id" element={<EditorMapPage />} />
          </Route>

          <Route element={<AppLayout />}>
            {/* Auth-flow pages: hidden from already-authenticated users */}
            <Route element={<PublicOnly />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Route>

            {/* Always public */}
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Auth-required app pages */}
            <Route element={<RequireAuth />}>
              <Route path="/setup" element={<SetupGamePage />} />
              <Route path="/play" element={<PlayPage />} />
              <Route path="/users/:id" element={<UserPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/games" element={<Games2Page />} />
              <Route path="/my-games" element={<MyGamesPage />} />
              <Route path="/campaign" element={<CampaignPage />} />
              <Route path="/campaign/:id" element={<CampaignMissionPage />} />
              <Route path="/editor" element={<EditorPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>

            {/* Root: public landing page (guest "Start Campaign") when
                logged out, otherwise straight to My Games. */}
            <Route
              path="/"
              element={
                auth ? <Navigate to="/my-games" replace /> : <HomePage />
              }
            />
            <Route
              path="*"
              element={<Navigate to={auth ? '/my-games' : '/login'} replace />}
            />
          </Route>
        </Routes>
      </AuthContext.Provider>
    </div>
  );
}

export default App;
